/**
 * The deck/view controls in the report filter bar — Version, Deck, Scale, and Adjustment/Currency
 * where the deck states them.
 *
 * Rendered through [[ReportFilterField]], the same chip the rest of the bar uses, so they are
 * indistinguishable from a filter to look at. They are NOT filters: none has a fact column, and
 * binding one as a predicate would AND against a column the fact does not have, matching nothing and
 * returning an all-empty report. The engine keeps them in their own list for exactly that reason.
 *
 * Read-only while `read_only` is set: switching data vintage has no query concept yet, and a control
 * that looks interactive but moves no number is worse than an honest fixed value.
 */

import React from 'react';
import Select from '@amzn/awsui-components-core/select';
import type { ViewControl } from 'src/components/star-grid/api/reportEngineTypes';
import { ReportFilterField } from 'src/components/star-grid/components/ReportFilterField';
import styles from 'src/components/star-grid/components/styles/SavedReportFilters.module.scss';

export interface ReportViewControlsProps {
  readonly controls: readonly ViewControl[];
  /**
   * Options and a handler per control id, for the ones that have a real query or display concept.
   *
   * Only some can act. `Version`, `Deck`, `Scale`, `Currency` and `Adjustment` are all UNMAPPED
   * dimensions — none has a fact column — so none can be a fact predicate; binding one would AND
   * against a column the fact does not have and match nothing. What they CAN drive is the display scale
   * (client-side), the deck the query runs against, and the column currency. A control with no entry
   * here stays a fixed value, which is honest: a dropdown that changes no number is worse.
   */
  readonly interactive?: Readonly<
    Record<
      string,
      {
        options: readonly { value: string; label: string }[];
        onChange: (value: string) => void;
        /**
         * The control's CURRENT value, when it differs from what the deck states.
         *
         * Scale is the case: the deck states a display string (`$ in MM's | Unit in 000's`) while the
         * options are scale ids, so without this the chip showed the deck's string as selected and no
         * option ever matched it.
         */
        value?: string;
      }
    >
  >;
  /**
   * A control rendered by a purpose-made component instead of a dropdown, keyed by control id.
   *
   * `Version` is the case: its snapshot picker has search, Live, tagged snapshots, a date range and a
   * tree of grouped history, because a deck has hundreds of vintages. The node supplies its own chip, so it is rendered in place of one.
   */
  readonly custom?: Readonly<Record<string, React.ReactNode>>;
}

export const ReportViewControls: React.FC<ReportViewControlsProps> = ({ controls, interactive, custom }) => (
  <>
    {controls.map((control) => {
      const supplied = custom?.[control.id];
      if (supplied) return <React.Fragment key={control.id}>{supplied}</React.Fragment>;
      const action = interactive?.[control.id];
      // A control the caller wired gets a real picker; one it did not stays the deck's fixed value.
      // Both go through the SAME chip, so the bar reads as one row of controls either way.
      if (!action || action.options.length < 2) {
        return (
          <ReportFilterField
            key={control.id}
            label={control.label}
            controlId={`report-view-control-${control.id}`}
            value={control.value}
            dropdown={false}
          >
            <span className={styles.fixedValue} data-testid={`report-view-control-${control.id}`}>
              {control.value}
            </span>
          </ReportFilterField>
        );
      }
      // `value` wins over `control.value` where they speak different vocabularies: Scale's options are
      // scale ids while the deck states a display string, so matching on the deck's string selects nothing.
      const selected = action.options.find((option) => option.value === (action.value ?? control.value)) ?? null;
      return (
        <ReportFilterField
          key={control.id}
          label={control.label}
          controlId={`report-view-control-${control.id}`}
          value={selected?.label ?? control.value}
          // The chip draws its own chevron for a plain-button child, but a Cloudscape Select brings
          // one — both rendered meant two stacked arrows with only the Select's opening anything.
          dropdown={false}
        >
          <Select
            controlId={`report-view-control-${control.id}`}
            // Portals the panel out of the filter bar, which clips overflow — without it the panel
            // opened INSIDE the bar and was invisible, the same cause as the line set's deck picker.
            expandToViewport
            selectedOption={selected}
            options={[...action.options]}
            ariaLabel={control.label}
            placeholder={control.value}
            data-testid={`report-view-control-${control.id}`}
            onChange={({ detail }) => {
              const next = detail.selectedOption.value;
              if (next !== undefined) action.onChange(next);
            }}
          />
        </ReportFilterField>
      );
    })}
  </>
);
